


import React, { useState } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { selectTransactions, selectMode, selectMerchant } from "../../../redux/merchantSlice";

const P = {
  white: "#FFFFFF", snow: "#FAFAFA", pearl: "#F5F5F0", cloud: "#E8E5DE",
  mist: "#D4D0C8", silver: "#B0ADA6", steel: "#8A8780", graphite: "#5C5955",
  ink: "#1A1815", gold: "#C9A84C", goldDark: "#9A7A2E",
  success: "#2D8B5F", successBg: "#E8F5EE", error: "#C44D4D", errorBg: "#FDE8E8",
  warn: "#D4952B", warnBg: "#FEF4E5", info: "#4A7FC4",
};
const serif = "'Playfair Display', Georgia, serif";

const fmt = (n: number, c = "eur") => {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: c.toUpperCase(), minimumFractionDigits: 2 }).format(n); }
  catch { return `${c.toUpperCase()} ${n.toFixed(2)}`; }
};

const statusColor = (s: string) => {
  switch (s) {
    case "succeeded": return { bg: P.successBg, fg: P.success };
    case "failed":    return { bg: P.errorBg,   fg: P.error };
    case "pending":   return { bg: P.warnBg,    fg: P.warn };
    default:          return { bg: `${P.info}11`, fg: P.info };
  }
};

export default function Transactions() {
  const transactions = useSelector(selectTransactions);
  const mode         = useSelector(selectMode);
  const merchant     = useSelector(selectMerchant);
  const [filter, setFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");

  const filtered = transactions.filter(tx => {
    if (filter !== "all" && tx.status !== filter) return false;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      return (tx.customer_email?.toLowerCase().includes(q) || tx.customer_name?.toLowerCase().includes(q) || tx.id.toLowerCase().includes(q));
    }
    return true;
  });

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 24, fontFamily: serif, fontWeight: 700, color: P.ink, marginBottom: 6 }}>Transactions</h2>
        <p style={{ fontSize: 13, color: P.steel }}>{transactions.length} total transactions in {mode} mode.</p>
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search by email, name, or ID…"
          style={{ flex: 1, minWidth: 220, padding: "10px 14px", borderRadius: 8, border: `1px solid ${P.cloud}`, fontSize: 12, color: P.ink, outline: "none", background: P.white, fontFamily: "'DM Sans', sans-serif", transition: "border-color .2s" }}
          onFocus={e => (e.target.style.borderColor = P.gold)} onBlur={e => (e.target.style.borderColor = P.cloud)} />
        <div style={{ display: "flex", gap: 4 }}>
          {["all", "succeeded", "pending", "failed"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: "8px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600,
              border: `1px solid ${filter === f ? P.gold + "44" : P.cloud}`,
              background: filter === f ? `${P.gold}0D` : P.white,
              color: filter === f ? P.goldDark : P.steel,
              cursor: "pointer", textTransform: "capitalize" as const, transition: "all .2s",
              fontFamily: "'DM Sans', sans-serif",
            }}>{f}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: P.white, borderRadius: 14, border: `1px solid ${P.cloud}`, overflow: "hidden" }}>
        {/* Table header */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 1fr 0.8fr", padding: "12px 24px", borderBottom: `1px solid ${P.pearl}`, gap: 12 }}>
          {["Customer", "Amount", "Status", "Method", "Date"].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: P.silver, textTransform: "uppercase" as const }}>{h}</div>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.25 }}>⟐</div>
            <div style={{ fontSize: 13, color: P.silver }}>No transactions found.</div>
          </div>
        ) : (
          filtered.slice(0, 30).map((tx, i) => {
            const sc = statusColor(tx.status);
            return (
              <motion.div key={tx.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 1fr 0.8fr", padding: "14px 24px", borderBottom: `1px solid ${P.pearl}`, alignItems: "center", gap: 12, cursor: "pointer", transition: "background .15s" }}
                onMouseEnter={e => (e.currentTarget.style.background = P.snow)} onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: P.ink }}>{tx.customer_email || tx.customer_name || "—"}</div>
                  <div style={{ fontSize: 10, color: P.silver, marginTop: 2 }}>{tx.id.slice(0, 20)}…</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: P.ink }}>{fmt(tx.amount / 100, tx.currency)}</div>
                <div>
                  <span style={{ padding: "4px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.fg, textTransform: "capitalize" as const }}>{tx.status}</span>
                </div>
                <div style={{ fontSize: 12, color: P.graphite, textTransform: "capitalize" as const }}>{tx.payment_method_types?.[0] || "card"}</div>
                <div style={{ fontSize: 11, color: P.silver }}>{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : "—"}</div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}